import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight } from '@phosphor-icons/react';
import axios from 'axios';
import { API, CONTACT_INFO } from '../App';

const Home = () => {
  const [concepts, setConcepts] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [loading, setLoading] = useState(true);

  // Hero slider images - Updated with new bar photos
  const heroImages = [
    "https://customer-assets.emergentagent.com/job_signature-studio-4/artifacts/s3v7a62c_STUDIOBAR70.png",
    "https://customer-assets.emergentagent.com/job_signature-studio-4/artifacts/lvix9nl8_ChatGPT%20Image%2029%20avr.%202026%20a%CC%80%2012_06_45.png",
    "https://customer-assets.emergentagent.com/job_signature-studio-4/artifacts/nebnxk7d_4.png",
    "https://customer-assets.emergentagent.com/job_signature-studio-4/artifacts/p810oqw4_ChatGPT%20Image%2029%20avr.%202026%20a%CC%80%2010_50_24.png"
  ];

  useEffect(() => {
    const fetchConcepts = async () => {
      try {
        const response = await axios.get(`${API}/concepts/featured`);
        setConcepts(response.data.slice(0, 4));
      } catch (error) {
        console.error('Failed to fetch concepts:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchConcepts();
    axios.post(`${API}/seed`).catch(() => {});
  }, []);

  // Auto-slide - faster interval (3 seconds instead of 5)
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroImages.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [heroImages.length]);

  return (
    <div className="bg-black">
      {/* Hero Section with Slider */}
      <section 
        data-testid="hero-section"
        className="relative h-screen flex items-center justify-center overflow-hidden"
      >
        {/* Background Slider - Dynamic slide animation */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentSlide}
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '-100%' }}
            transition={{ duration: 0.8, ease: 'easeInOut' }}
            className="absolute inset-0"
          >
            <img
              src={heroImages[currentSlide]}
              alt="Studio Bar"
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-black/60" />
          </motion.div>
        </AnimatePresence>

        {/* Content - STUDIOBAR as H1 */}
        <div className="relative z-10 max-w-5xl mx-auto px-6 lg:px-12 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="font-display text-5xl sm:text-6xl lg:text-8xl tracking-tight text-white mb-6"
          >
            STUDIOBAR
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="font-display text-xl sm:text-2xl lg:text-3xl text-white/90 mb-4 tracking-wide"
          >
            Des expériences cocktail scénographiées<br />pour vos événements
          </motion.p>
          
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="text-base text-white/60 max-w-2xl mx-auto mb-4 leading-relaxed"
          >
            STUDIO BAR conçoit des prestations cocktail clés en main, combinant bars design, barmen et création de cocktails pour transformer chaque événement en expérience.
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-sm text-white/40 italic"
          >
            Prestation complète ou mise à disposition pour professionnels
          </motion.p>
        </div>

        {/* Slide Indicators */}
        <div className="absolute bottom-12 left-1/2 -translate-x-1/2 flex gap-2">
          {heroImages.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-2 h-2 rounded-full transition-all ${
                currentSlide === index ? 'bg-white w-8' : 'bg-white/30'
              }`}
            />
          ))}
        </div>
      </section>

      {/* Section 1 - Concept */}
      <section className="section-white py-32">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="px-6 lg:px-0"
            >
              <h2 className="font-display text-3xl lg:text-4xl text-black mb-3">
                Le concept Studio Bar
              </h2>
              <div className="w-12 h-px bg-black/30 mb-4"></div>
              <p className="text-sm uppercase tracking-wider text-gray-500 mb-8">Concept</p>
              <p className="text-gray-600 leading-relaxed mb-6">
                STUDIO BAR conçoit des expériences autour du bar, pensées comme des éléments centraux de l'événement.
              </p>
              <p className="text-gray-600 leading-relaxed mb-6">
                Chaque projet associe design, fonctionnalité et qualité de service pour créer une présence forte et cohérente.
              </p>
              <p className="text-gray-600 leading-relaxed mb-8">
                Nos formats s'adaptent à votre espace, à votre configuration et à l'expérience recherchée.
              </p>
              <Link to="/concept" className="btn-primary-dark inline-flex items-center gap-2">
                Découvrir
                <ArrowRight size={16} weight="light" />
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="aspect-[4/5] overflow-hidden bg-gray-100">
                <img
                  src="https://customer-assets.emergentagent.com/job_signature-studio-4/artifacts/ho2yg9g9_test%20plastic%20texture.png"
                  alt="Studio Bar Concept"
                  className="w-full h-full object-cover object-right"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Section 2 - Bar Mobile */}
      <section className="bg-black py-32">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="lg:order-2 px-6 lg:px-0"
            >
              <h2 className="font-display text-3xl lg:text-4xl text-white mb-3">
                Bars prêts à installer
              </h2>
              <div className="w-12 h-px bg-white/30 mb-4"></div>
              <p className="text-sm uppercase tracking-wider text-white/50 mb-8">Bar Mobile</p>
              <p className="text-white/60 leading-relaxed mb-6">
                Une sélection de bars design disponibles immédiatement, conçus pour s'intégrer avec élégance à différents formats d'événements.
              </p>
              <p className="text-white/60 leading-relaxed mb-6">
                Des lignes sobres, des finitions soignées et une installation pensée pour allier esthétique et efficacité.
              </p>
              {/* SEO phrase - Bold white text */}
              <p className="text-white font-semibold text-lg mb-8">
                Des bars scénographiés à partir de 2,40 m, disponibles en prestation complète ou en mise à disposition pour professionnels.
              </p>
              <Link to="/bar-mobile" className="btn-primary inline-flex items-center gap-2">
                Voir la collection
                <ArrowRight size={16} weight="light" />
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="lg:order-1"
            >
              <div className="aspect-[4/5] overflow-hidden bg-gray-900">
                <img
                  src="https://customer-assets.emergentagent.com/job_signature-studio-4/artifacts/2mtdx7fo_Capture%20d%E2%80%99e%CC%81cran%202026-04-29%20a%CC%80%2010.02.12.png"
                  alt="Bar Mobile"
                  className="w-full h-full object-cover object-center"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Section 3 - Studio Créa */}
      <section className="section-white py-32">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="px-6 lg:px-0"
            >
              <h2 className="font-display text-3xl lg:text-4xl text-black mb-3">
                Création sur mesure
              </h2>
              <div className="w-12 h-px bg-black/30 mb-4"></div>
              <p className="text-sm uppercase tracking-wider text-gray-500 mb-8">Studio Créa</p>
              <p className="text-gray-600 leading-relaxed mb-6">
                Nous imaginons et fabriquons des bars sur mesure selon votre projet.
              </p>
              <p className="text-gray-600 leading-relaxed mb-8">
                Dimensions, matériaux, finitions et identité visuelle : chaque détail est conçu pour créer une pièce forte, au service de votre événement.
              </p>
              <Link to="/studio-crea" className="btn-primary-dark inline-flex items-center gap-2">
                Découvrir
                <ArrowRight size={16} weight="light" />
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              {/* Image Conception uniquement */}
              <div className="space-y-2">
                <div className="aspect-square overflow-hidden bg-white flex items-center justify-center">
                  <img
                    src="https://customer-assets.emergentagent.com/job_signature-studio-4/artifacts/2l0b5584_ChatGPT%20Image%2029%20avr.%202026%20a%CC%80%2012_46_09.png"
                    alt="Conception - Croquis"
                    className="w-full h-full object-contain"
                  />
                </div>
                <p className="text-xs text-gray-500 text-center uppercase tracking-wider px-6 lg:px-0">Conception</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Section 4 - Prestation Cocktail - Harmonisé avec les autres sections */}
      <section className="bg-black py-32">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">
            {/* Image cocktail */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="aspect-[4/5] overflow-hidden">
                <img
                  src="https://customer-assets.emergentagent.com/job_signature-studio-4/artifacts/ev2j7asv_ChatGPT%20Image%2029%20avr.%202026%20a%CC%80%2010_16_10.png"
                  alt="Service Cocktail"
                  className="w-full h-full object-cover object-center hover:scale-105 transition-transform duration-700"
                />
              </div>
            </motion.div>

            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="px-6 lg:px-0"
            >
              <h2 className="font-display text-3xl lg:text-4xl text-white mb-3">
                Service cocktail
              </h2>
              <div className="w-12 h-px bg-white/30 mb-4"></div>
              <p className="text-sm uppercase tracking-wider text-white/50 mb-8">Prestation Cocktail</p>
              <p className="text-white/60 leading-relaxed mb-6">
                STUDIO BAR propose des prestations cocktail complètes, pensées comme une expérience à part entière.
              </p>
              <p className="text-white/60 leading-relaxed mb-6">
                Barmen, création de cocktails et mise en scène du bar : chaque détail est conçu pour allier fluidité, esthétique et impact.
              </p>
              <p className="text-white font-semibold text-lg mb-8">
                Une expérience cocktail complète, adaptée à votre événement.
              </p>
              <Link to="/prestation-cocktail" className="btn-primary inline-flex items-center gap-2">
                En savoir plus
                <ArrowRight size={16} weight="light" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
